import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import st from './AutoBotEditModal.module.scss'
import { WhatsAppPreview } from './WhatsAppPreview'
import { HighlightedOverlay } from './HighlightedOverlay'
import { MAX_LEN, findUnknownVars, fillTemplate } from './constants'
import cogoToast from 'cogo-toast'

export const AutoBotEditModal = ({ bot, onClose, onSave }) => {
  const [text, setText] = useState(bot.text)
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef(null)

  const availableVars = useMemo(
    () => bot.available_variables || [],
    [bot.available_variables],
  )

  const unknownVars = useMemo(
    () => findUnknownVars(text, availableVars),
    [text, availableVars],
  )

  const isEmpty = text.trim().length === 0
  const tooLong = text.length > MAX_LEN
  const canSave = !isEmpty && unknownVars.length === 0 && !tooLong && !isSaving

  const previewText = useMemo(() => fillTemplate(text), [text])

  const handleSave = useCallback(async () => {
    if (!canSave) return
    setIsSaving(true)
    try {
      await onSave({ ...bot, text, available_variables: availableVars })
      onClose()
    } catch (e) {
      console.error('[AutoBotEditModal] save failed', {
        message: e.message,
        response: e.response?.data,
        config: e.config,
      })
      cogoToast.error(
        e?.response?.data?.detail || 'Не удалось сохранить шаблон',
      )
    } finally {
      setIsSaving(false)
    }
  }, [canSave, onSave, onClose, bot, text, availableVars])

  // Esc — закрыть, Ctrl/Cmd+Enter — сохранить
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canSave) {
        handleSave()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [canSave, handleSave, onClose])

  const insertVariable = (varName) => {
    const ta = textareaRef.current
    const token = `{${varName}}`

    if (!ta) {
      const next = `${text}{${varName}}`
      if (next.length <= MAX_LEN) setText(next)
      return
    }

    const start = ta.selectionStart ?? text.length
    const end = ta.selectionEnd ?? text.length
    const next = text.slice(0, start) + token + text.slice(end)
    if (next.length > MAX_LEN) return

    setText(next)
    requestAnimationFrame(() => {
      ta.focus()
      const pos = start + token.length
      ta.setSelectionRange(pos, pos)
    })
  }

  return (
    <div className={st.overlay} onClick={onClose}>
      <div
        className={st.modal}
        onClick={(e) => e.stopPropagation()}
        role='dialog'
        aria-modal='true'
      >
        <header className={st.header}>
          <div>
            <h3 className={st.title}>{bot.name}</h3>
            <p className={st.trigger}>{bot.trigger}</p>
          </div>
          <button className={st.close} onClick={onClose} aria-label='Закрыть'>
            ×
          </button>
        </header>

        <div className={st.columns}>
          <div className={st.editor}>
            <label className={st.label}>Текст сообщения</label>

            <div className={st.editorWrap}>
              <HighlightedOverlay text={text} availableVars={availableVars} />
              <textarea
                ref={textareaRef}
                className={st.textarea}
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={12}
                placeholder='Введите текст сообщения…'
              />
            </div>

            <div className={st.meta}>
              <span className={tooLong ? st.errorText : st.hint}>
                {text.length} / {MAX_LEN}
              </span>
              {unknownVars.length > 0 && (
                <span className={st.errorText}>
                  Неизвестные переменные:{' '}
                  {unknownVars.map((v) => `{${v}}`).join(', ')}
                </span>
              )}
            </div>

            <label className={st.label}>Доступные переменные</label>
            <div className={st.vars}>
              {availableVars.length === 0 && (
                <span className={st.hint}>Нет переменных для этого бота</span>
              )}
              {availableVars.map((v) => (
                <button
                  key={v}
                  type='button'
                  className={st.varBtn}
                  onClick={() => insertVariable(v)}
                >
                  {`{${v}}`}
                </button>
              ))}
            </div>
          </div>

          <div className={st.preview}>
            <label className={st.label}>Предпросмотр</label>
            <WhatsAppPreview text={previewText} />
          </div>
        </div>

        <footer className={st.actions}>
          {/*<span className={st.kbdHint}>*/}
          {/*  <kbd>Ctrl</kbd>+<kbd>Enter</kbd> — сохранить, <kbd>Esc</kbd> —*/}
          {/*  отмена*/}
          {/*</span>*/}
          <div className={st.actionsRight}>
            <button className={st.cancel} onClick={onClose}>
              Отмена
            </button>
            <button
              className={st.save}
              disabled={!canSave}
              onClick={handleSave}
            >
              {isSaving ? 'Сохранение…' : 'Сохранить'}
            </button>
          </div>
        </footer>
      </div>
    </div>
  )
}
